import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Paper,
  CircularProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { toast } from 'react-hot-toast';

const validationSchema = Yup.object({
  termsAccepted: Yup.boolean()
    .oneOf([true], 'You must accept the terms and conditions')
    .required('You must accept the terms and conditions'),
});

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api'
  : 'http://localhost:5001/api';

const PaymentConfirmation = ({ bookingSummary, onNext, onBack }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const formik = useFormik({
    initialValues: {
      termsAccepted: false,
      method: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsProcessing(true);
      try {
        const bookingData = {
          ...bookingSummary,
          payment: { method: 'invoice' },
          status: 'confirmed',
          timestamp: new Date().toISOString(),
        };

        // Remove the individual price field as it's now included in pricing breakdown
        delete bookingData.price;

        console.log('Submitting booking data:', bookingData);

        const response = await fetch(`${API_BASE_URL}/booking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bookingData)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Server Error:', errorData);
          throw new Error(`Failed to confirm booking: ${errorData.message || response.statusText}`);
        }

        const data = await response.json();
        
        onNext({ 
          ...bookingData,
          reference: data.bookingReference,
          confirmationMessage: data.message,
          payment: { 
            method: 'invoice',
            status: 'confirmed',
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Failed to process booking:', error);
        toast.error(error.message || 'Failed to process booking. Please try again.', {
          duration: 5000,
          position: 'top-center',
        });
      } finally {
        setIsProcessing(false);
      }
    },
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ 
            bgcolor: 'background.dark',
            color: 'common.white',
            p: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Booking Summary
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Vehicle Details */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: 'text.primary',
                      fontWeight: 500,
                      mb: 1
                    }}>
                      <DirectionsCarIcon sx={{ mr: 1 }} />
                      Selected Vehicle
                    </Typography>
                    <Typography variant="body1" sx={{ pl: 4 }}>
                      {bookingSummary.vehicle?.name}
                    </Typography>
                  </Box>

                  {/* Journey Details */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: 'text.primary',
                      fontWeight: 500,
                      mb: 1
                    }}>
                      <LocationOnIcon sx={{ mr: 1 }} />
                      Journey Details
                    </Typography>
                    
                    {/* Outbound Journey */}
                    <Box sx={{ pl: 4, mb: bookingSummary.pickup.hasReturnJourney ? 2 : 0 }}>
                      {bookingSummary.pickup.hasReturnJourney && (
                        <Typography variant="subtitle2" sx={{ 
                          color: 'primary.main', 
                          fontWeight: 600, 
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          🚗 Outbound Journey
                        </Typography>
                      )}
                      <Box sx={{ pl: bookingSummary.pickup.hasReturnJourney ? 2 : 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body1">
                          From: {bookingSummary.pickup.location}
                        </Typography>
                        <Typography variant="body1">
                          To: {bookingSummary.pickup.dropoff}
                        </Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <AccessTimeIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                          Duration: {bookingSummary.pickup.duration} hours
                        </Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <GroupIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                          Passengers: {bookingSummary.pickup.passengers}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          Pickup Date & Time: {new Date(bookingSummary.pickup.dateTime).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          }).replace(',', '')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Return Journey */}
                    {bookingSummary.pickup.hasReturnJourney && (
                      <Box sx={{ pl: 4 }}>
                        <Typography variant="subtitle2" sx={{ 
                          color: 'success.main', 
                          fontWeight: 600, 
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          🔄 Return Journey
                        </Typography>
                        <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="body1">
                            From: {bookingSummary.pickup.returnLocation}
                          </Typography>
                          <Typography variant="body1">
                            To: {bookingSummary.pickup.returnDropoff}
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTimeIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                            Duration: {bookingSummary.pickup.duration} hours
                          </Typography>
                          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <GroupIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                            Passengers: {bookingSummary.pickup.passengers}
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            Return Date & Time: {new Date(bookingSummary.pickup.returnDateTime).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            }).replace(',', '')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  bgcolor: 'background.paper',
                  p: 3,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  textAlign: 'right'
                }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, width: '100%', fontSize: '1.1rem' }}>
                    Total Price
                  </Typography>
                  {bookingSummary.pickup.duration > 2 && (
                    <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                      10% discount applied
                    </Typography>
                  )}
                  {bookingSummary.pickup.hasReturnJourney && (
                    <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                      10% return journey discount applied
                    </Typography>
                  )}
                  {bookingSummary.discountInfo && (
                    <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                      Discount Code: {bookingSummary.discountInfo.message}
                    </Typography>
                  )}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'baseline',
                    justifyContent: 'flex-end',
                    gap: 1
                  }}>
                    <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                      £{bookingSummary.pricing ? bookingSummary.pricing.finalPrice.toFixed(2) : bookingSummary.price.toFixed(2)}
                    </Typography>
                  </Box>
                  {bookingSummary.pickup.hasReturnJourney && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
                      Includes both journeys
                    </Typography>
                  )}
                  {bookingSummary.discountInfo && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                      Discount Code: {bookingSummary.pickup.discountCode.toUpperCase()}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
              Payment & Confirmation
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Payment Method
                  </Typography>
                  <Box display="flex" gap={2} mb={3}>
                    <Button
                      variant="contained"
                      startIcon={<AccountBalanceIcon />}
                      sx={{ flex: 1 }}
                      onClick={() => {
                        formik.setFieldValue('method', 'invoice');
                      }}
                    >
                      Pay via Bank Transfer/Cash
                    </Button>
                  </Box>
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    bgcolor: 'background.paper', 
                    borderRadius: 1, 
                    border: '1px solid', 
                    borderColor: 'divider' 
                  }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      A £50 deposit is required to secure your booking. The bank details will be sent to your email after booking confirmation.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please ensure to use your booking reference as the payment reference when making the bank transfer.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.termsAccepted}
                        onChange={(e) => formik.setFieldValue('termsAccepted', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I accept the terms and conditions and agree to the cancellation policy
                      </Typography>
                    }
                  />
                  {formik.touched.termsAccepted && formik.errors.termsAccepted && (
                    <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                      {formik.errors.termsAccepted}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={onBack}
                      sx={{ flex: 1 }}
                      disabled={isProcessing}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!formik.values.termsAccepted || isProcessing}
                      sx={{ flex: 1 }}
                    >
                      {isProcessing ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Processing...
                        </>
                      ) : (
                        'Confirm Booking'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', color: 'text.primary' }}>
            Need Help?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            If you have any questions about your booking or need assistance, please don't hesitate to contact us:
          </Typography>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}>
              Email: info@eminentchauffeur.com
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}>
              Phone: +44 (0) 123 456 7890
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default PaymentConfirmation; 